import React, { useEffect, useState } from 'react'
import { Wifi, Check, ArrowRight, Calendar, BarChart3, Lightbulb, Loader2, AlertCircle } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'

const ConfirmationPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [orderData, setOrderData] = useState<any>(null)
  const [isCreatingAccount, setIsCreatingAccount] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const session_id = searchParams.get('session_id')
    
    if (session_id) {
      // Coming back from Stripe - create account (NO localStorage dependency)
      createAccountFromStripeSession(session_id)
    } else {
      // No session_id - redirect to signup
      navigate('/signup')
    }
  }, [searchParams, navigate])

  const createAccountFromStripeSession = async (session_id: string) => {
    console.log('Creating account from Stripe session:', session_id)
    setIsCreatingAccount(true)
    setError(null)

    try {
      // Call backend with ONLY session_id - all data retrieved from Stripe metadata
      const response = await axios.post('https://flowbeatiq.com/api/billing/complete-signup', {
        session_id: session_id
      })

      console.log('Account creation response:', response.data)

      // Store auth tokens
      localStorage.setItem('auth_token', response.data.access_token)
      localStorage.setItem('auth_refresh_token', response.data.refresh_token)
      
      if (response.data.user) {
        localStorage.setItem('auth_user', JSON.stringify(response.data.user))
      }

      // Set order data for display (from server response)
      setOrderData({
        businessName: response.data.user.business_name,
        email: response.data.user.email,
        locationCount: '1'  // Default, could come from metadata if needed
      })

      // Clean up old localStorage (if any)
      localStorage.removeItem('signupData')
      localStorage.removeItem('orderData')

      console.log('Account created successfully:', response.data.user)
      setIsCreatingAccount(false)
      
    } catch (err: any) {
      console.error('Account creation error:', err)
      const errorMsg = err.response?.data?.detail || 'Failed to create account. Please contact support.'
      setError(errorMsg)
      setIsCreatingAccount(false)
    }
  }

  const handleAccessDashboard = () => {
    setIsRedirecting(true)
    window.location.href = 'https://flowbeatiq.com/dashboard'
  }

  const timeline = [
    {
      week: 1,
      title: 'Basic counts appear',
      description: 'Your sensor starts detecting visitor traffic',
      icon: BarChart3,
      color: 'blue'
    },
    {
      week: 2,
      title: 'Traffic patterns emerge',
      description: 'Peak hours and daily trends become visible',
      icon: Calendar,
      color: 'green'
    },
    {
      week: 3,
      title: 'Actionable insights begin',
      description: 'AI recommendations and business optimizations',
      icon: Lightbulb,
      color: 'purple'
    }
  ]

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-600'
      case 'green':
        return 'bg-green-600'
      case 'purple':
        return 'bg-purple-600'
      default:
        return 'bg-gray-600'
    }
  }

  // Show loading state while creating account
  if (isCreatingAccount) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Creating Your Account...</h2>
          <p className="text-gray-600">Processing your payment and setting up your dashboard</p>
        </div>
      </div>
    )
  }

  // Show error state
  if (error && !orderData) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">Account Creation Failed</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/signup')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
            >
              Start Over
            </button>
            <a
              href="mailto:support@flowbeatiq.com"
              className="block w-full text-center bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    )
  }

  if (!orderData) return null

  return (
    <div className="min-h-screen bg-green-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Wifi className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">FlowBeatIQ</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 sm:p-12 text-center">
          <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Check className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            You're in! Your sensor is on the way.
          </h1>

          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-semibold text-gray-900 mb-4">Order Details:</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Business:</span>
                <span className="font-semibold">{orderData.businessName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-semibold">{orderData.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Locations:</span>
                <span className="font-semibold">{orderData.locationCount}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-4">
                <span className="text-gray-900 font-semibold">Monthly Total:</span>
                <span className="font-bold text-green-600">$99/month</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">After 14-day free trial</p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">What to Expect:</h2>
            <div className="space-y-6">
              {timeline.map((item, index) => {
                const Icon = item.icon
                return (
                  <div key={index} className="flex items-start gap-4 text-left">
                    <div className={`w-10 h-10 ${getColorClasses(item.color)} text-white rounded-full flex items-center justify-center flex-shrink-0`}>
                      <span className="text-sm font-bold">{item.week}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-5 h-5 text-gray-600" />
                        <div className="font-semibold text-gray-900">Week {item.week}: {item.title}</div>
                      </div>
                      <div className="text-sm text-gray-600">{item.description}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="mb-8">
            <p className="text-lg text-gray-600 mb-6">
              Your sensor will arrive in 3-5 business days with setup instructions.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800">
                <strong>Important:</strong> Your free trial starts when your sensor arrives and is connected, not today.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <button 
              onClick={handleAccessDashboard}
              disabled={isRedirecting}
              className="w-full sm:w-auto bg-blue-600 text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2 mx-auto"
            >
              {isRedirecting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Redirecting...
                </>
              ) : (
                <>
                  Access Your Dashboard
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
            
            <div className="text-center">
              <button
                onClick={() => navigate('/')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Return to Homepage
              </button>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-gray-600">
              Questions? Email us at{' '}
              <a href="mailto:support@flowbeatiq.com" className="text-blue-600 hover:text-blue-700">
                support@flowbeatiq.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmationPage
